import { AxiosResponse } from 'axios';
import ApiUrls from '../config/ApiUrls';
import { replaceByObject } from '../utils/Urls';
import API, { ResourceStatus } from './Api';

export type EntityApiResponse = {
  id: number;
  name: string;
  legalName: string;
  paysTax: boolean;
  country: string;
  currency: string;
  organisationType: string;
  registrationNumber: string;
  taxNumber: string;
  financialYearEndDay: number;
  financialYearEndMonth: number;
  salesTaxBasis: string;
  salesTaxPeriod: string;
  defaultSalesTax: string;
  defaultPurchasesTax: string;
  sourceCreated: Date;
  timezone: string;
  shortCode: string;
  industryType: string;
  headcount: number;
  lockDate: string;
  currencies: string[];
  hasCompletedDataImport: boolean;
  hasCompletedTransactionImport: boolean;
  rateLimitDateTime: string;
};

export type ValuesApiResponse = {
  nominalCodes: NominalCodeApiResponse[],
  trackingCategories: TrackingCategoryApiResponse[],
  contacts: ContactApiResponse[],
  taxRates: TaxRateApiResponse[],
};

export type ValueApiResponse = {
  name: string,
  externalId: string,
  status: ResourceStatus,
};

export type NominalCodeApiResponse = ValueApiResponse & {
  code: string,
  type: string,
  systemAccount: boolean,
};

export type TrackingCategoryApiResponse = ValueApiResponse & {
  type: string,
};

export type ContactApiResponse = ValueApiResponse & {
  supplier: boolean,
  customer: boolean,
  httpStatus: number,
};

export type TaxRateApiResponse = {
  displayRate: number;
  effectiveRate: number;
  id: number;
  name: string;
  status: string;
  taxType: string;
  canApplyToAssets: boolean;
  canApplyToEquity: boolean;
  canApplyToExpenses: boolean;
  canApplyToLiabilities: boolean;
  canApplyToRevenue: boolean;
};

export type SuccessfulEntityResponse = {
  status: string,
  organisation: EntityApiResponse,
};

export class EntityApi extends API {
  static updateEntity(groupId: number, entityId: number, data: Partial<EntityApiResponse>) {
    return API.put(
      replaceByObject(ApiUrls.UpdateEntity, { groupId, entityId }),
      data
    ).then((response: AxiosResponse<SuccessfulEntityResponse>) => response.data.organisation);
  }

  static updateGroup(entityId: number, groupId: number, newGroupId: number): Promise<EntityApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.UpdateEntityGroup, { groupId, entityId }),
      {},
      {
        params: { newGroupId },
      }
    ).then((response: AxiosResponse<EntityApiResponse>) => response.data);
  }

  static loadValues(groupId: number, entityId: number): Promise<ValuesApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.EntityValues, { groupId, entityId })
    ).then((response: AxiosResponse<ValuesApiResponse>) => response.data);
  }

  static createContact(groupId: number, entityId: number, contactName: string): Promise<ContactApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.EntityContacts, { groupId, entityId }),
      {
        name: contactName,
      }
    ).then((response: AxiosResponse<ContactApiResponse>) => {
      const responseData = response.data;
      const httpStatus = response.status;
      return Object.assign({}, responseData, { httpStatus });
    });
  }
}
